"use client";

import { Skeleton } from "@tripcart/traveler-ui";
import dynamic from "next/dynamic";
import {
  Component,
  useMemo,
  Fragment,
  Suspense,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type Dispatch,
  type ErrorInfo,
  type ReactNode,
  type SetStateAction,
} from "react";

import { BookingSidebar } from "./_booking-sidebar";
import { useDateFromAddress } from "./_date-from-address";
import type { DepartureExtras } from "./_departures/departure-details";
import { resolveBookingConfigState } from "./_booking-config";
import { resolveQuoteAction } from "./_resolve-quote-action";
import { DeparturesSection } from "./_departures/departures-section.client";
import { TourTabsBar } from "./_tour-tabs-bar.client";
import { TourBottomTabs } from "./_tour-bottom-tabs.client";
import {
  orderSections,
  layoutAroundFaq,
  splitBodyForRail,
  type TourTab,
} from "./_section-order";
import { useAvailability } from "./_use-availability";
import type { AvailabilityPayload } from "./page";
import type { ApiFetchResult } from "@/lib/api-client";
import { hasDisplayPrice } from "@/lib/price-display";
import { seedTravelersForPackage } from "@tripcart/shared/pricing/booking-math";

import { ExperienceMediaGalleryProvider } from "@/components/experience-media-gallery";
import { SITE_CONTAINER } from "@/components/container";

import { cx } from "@tripcart/traveler-ui/cx";
import styles from "./_experience-detail-view.module.css";

import { useTranslator } from "@/lib/translation-context";
import { useFormatDate } from "@/lib/datetime-format-context";
import type { TravelersState } from "@/lib/cart/types";

import { resolveBookingPricing } from "./_active-pricing";
import { ExternalBookingContext } from "./_external-booking/external-booking-context";
import { ExternalBookingRail } from "./_external-booking/external-booking-rail";
import { type MediaItem } from "@/lib/experience-media";
import { trackAdsEvent } from "@/lib/ads-events";
import type { DepartureDto, ExperienceDto } from "@tripcart/shared";

// BookingSidebar is the above-the-fold interactive element on this page.
// Lazy-loading it via next/dynamic risks chunk-load failures that leave the
// SSR'd HTML on screen with no event handlers attached (all four controls
// appear rendered but clicks die). The expensive piece — react-day-picker
// + date-fns — stays lazy via its own dynamic() inside _booking-sidebar.tsx.

// Render-time crashes inside the sidebar (e.g. a real-API DTO shape the
// component doesn't handle) would otherwise unmount the React subtree and
// leave the SSR snapshot in place looking interactive but dead. A visible
// retry surface is strictly better than silent inertness.
class BookingSidebarErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Literal process.env.NODE_ENV (Next inlines it) avoids importing
    // @/env.client, which pulls all of zod into this hydration chunk.
    // eslint-disable-next-line no-restricted-properties -- the suggested fix (import the validated env) is exactly the zod pull we're avoiding
    if (process.env.NODE_ENV !== "production") {
      console.error("[BookingSidebar] render error", error, info);
    }
  }
  override render() {
    if (this.state.error) {
      return <BookingSidebarErrorFallback />;
    }
    return this.props.children;
  }
}

function BookingSidebarErrorFallback() {
  const t = useTranslator();
  return (
    <div className={styles.sidebarError}>
      <p className={styles.sidebarErrorTitle}>
        {t("Booking widget failed to load.")}
      </p>
      <p className={styles.sidebarErrorText}>
        {t("Please refresh the page to try again.")}
      </p>
    </div>
  );
}

function PackagesSkeleton() {
  return (
    <div className={styles.packagesSkeleton}>
      {[0, 1, 2].map((i) => (
        <div key={i} className={styles.packageSkeleton}>
          <div className={styles.packageSkeletonRow}>
            <div className={styles.packageSkeletonLines}>
              <div className={cx(styles.bar, styles.barTitle)} />
              <div className={cx(styles.bar, styles.barWide)} />
              <div className={cx(styles.bar, styles.barHalf)} />
            </div>
            <div className={cx(styles.bar, styles.barButton)} />
          </div>
        </div>
      ))}
    </div>
  );
}

const PackagesSection = dynamic(
  () =>
    import("./_packages-section").then((m) => ({ default: m.PackagesSection })),
  { loading: () => <PackagesSkeleton /> },
);

// Still code-split, but SERVER-RENDERED. It was `ssr: false`, justified by
// "hydration mismatch on the `prefers-reduced-motion` / window-width checks the
// bar makes on mount" — the bar makes no such checks any more (the
// IntersectionObserver went with the reveal-on-scroll, and reduced motion is
// pure CSS). Keeping the flag would have left a stale reason guarding it.
// It has to paint with the page now that it is always on screen: it sits
// directly on the bottom tab bar, which IS server-rendered, and one half of
// that pair arriving a chunk-load later reads as a broken bar rather than as a
// fast one. Being `position: fixed`, it costs no layout shift either way.
const StickyBookBar = dynamic(() =>
  import("@/components/sticky-book-bar").then((m) => ({
    default: m.StickyBookBar,
  })),
);

/** Honour the user's reduced-motion preference for programmatic scrolls.
 *  Smooth-scroll animation burns CPU on mid-tier mobile (~30–60 ms INP)
 *  and matters for travellers who set OS-level reduced motion. */
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function")
    return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
//
// Phase 1D (perf/lighthouse-100): the hero (breadcrumbs, H1, meta row,
// bento gallery grid) used to live here as a 250-line client component.
// It now lives in `./_hero/hero.server.tsx` as a Server Component with
// two tiny client islands (gallery-tile, view-all-button). page.tsx
// renders it as a server child and passes it down via the `heroSlot`
// prop so the LCP image is in the initial HTML, and only the click
// handlers ship JS to the browser.

// The itinerary renders in ./_itinerary/itinerary.server.tsx and arrives here
// as `itinerarySlot` from page.tsx.

// ─── Main view ────────────────────────────────────────────────────────────────

function seedTravelersFromPackage(
  packages: ExperienceDto["packages"] | undefined,
): TravelersState {
  if (!packages?.length) return {};
  const pkg = packages.find((p) => p.isDefault) ?? packages[0]!;
  // Delegates to the shared seed function so the API and FE agree on
  // exactly what "default selection" means. Spec coverage lives in
  // packages/shared/src/pricing/booking-math.spec.ts.
  return { ...seedTravelersForPackage(pkg) };
}

// Hero + the read-only sections (description, cost, reviews, map, related,
// return) are rendered server-side by `page.tsx` and passed in as opaque
// ReactNode slots. This view stays client-side only for the surfaces that
// need browser state: itinerary accordion, FAQ accordion, BookingSidebar,
// StickyBookBar, and the gallery context provider.
export type ExperienceBookingData = Pick<
  ExperienceDto,
  | "id"
  | "slug"
  | "name"
  | "heroImage"
  | "packages"
  | "seasons"
  | "priceFrom"
  // The struck-through figure and the per-line beside it in the pinned price
  // bar (guide `.bb-amt s` / `.bb-amt i`). Both optional on the wire; the bar
  // renders neither rather than inventing a discount or a "per person" that is
  // wrong on a group-priced tour.
  | "priceFromWas"
  | "headlinePriceLabel"
  | "pricingMode"
  | "onRequest"
  | "hasGroupPricing"
  // Drives the deposit / balance-due line in the sidebar. Absent when the
  // operator has configured no payment policy — the line then renders nothing
  // rather than asserting a default.
  | "payment"
  // The rail's own star row (guide `.bk-rate`). Five small stars, the average,
  // and a jump link to the reviews — a different treatment from the hero's
  // single star, and deliberately so: this one sits on the thing that takes
  // money. Omitted whole when the tour has no reviews.
  | "rating"
  // Misc listing details surfaced in the booking area (AC-20).
  | "badge"
  | "soldAs"
  | "pricingHighlights"
  // Trip length, for the end date on each departure row. Absent when the
  // operator has not set one — the row then shows the start date alone rather
  // than inventing a return date.
  | "durationDays"
  // The departure row's expanded details. Optional on the wire and omitted
  // from the panel when absent, rather than falling back to a plausible
  // default — this panel is what a careful buyer reads before paying, so a
  // guessed group size here is worse than a shorter list.
  | "groupSize"
  // Present only when the tour books on the agency's own site. Every booking
  // CTA on the page follows it; absent is the TripCart checkout.
  | "externalBooking"
>;

/** Client-fetched initial availability slice (from `useAvailability`), threaded
 *  into the shared view by `ExperienceDetailWithLiveAvailability`. */
export interface LiveAvailabilityState {
  status: "pending" | "ready" | "error";
  departures: DepartureDto[] | null;
  windowEnd: string | null;
  hasMoreAfter: boolean;
  error?: string;
}

/** The shared view is seed-agnostic: it drives its sidebar + package cards from
 *  one of two availability sources, chosen by the wrapper that renders it.
 *
 *  - `"live"`  — a resolved hook state (the `sites` route, whose render path
 *    must create NO per-request server fetch so Task 5 can make it static).
 *  - `"promised"` — the server-created availability promise, unwrapped via
 *    React 19 `use()` DEEP at the sidebar/package boundaries (not above the
 *    view) so the hero + every server slot still stream in the initial
 *    document exactly as before — only those two subtrees suspend. */
export type AvailabilitySeed =
  | { mode: "live"; state: LiveAvailabilityState }
  | { mode: "promised"; promise: Promise<ApiFetchResult<AvailabilityPayload>> };

export interface ExperienceDetailViewProps {
  data: ExperienceBookingData | null;
  media: MediaItem[];
  availability: AvailabilitySeed;
  /** Storefront tab bar — ordered + visibility-filtered by the API (AC-16).
   *  When absent/empty the bar is suppressed and the sections fall back to the
   *  built-in default order (e.g. the operator preview, which carries no tabs). */
  tabs?: TourTab[];
  /** The tabs the operator switched ON, before per-experience content filtering.
   *  An empty `tabs` falls back to THIS rather than to the built-in order, so a
   *  tab they hid cannot reappear; empty means they hid everything, absent means
   *  an older API and keeps the default-order fallback. (This docstring
   *  previously described a boolean called `tabsConfigured` — "whether the agency
   *  configured tabs" — which is the semantics that blanked ordinary tours.) */
  visibleTabs?: readonly TourTab[];
  /** Server-rendered custom-tab sections (AC-18), keyed by tab slug. The
   *  registry slots each into its configured position by `tabs[]` order. */
  customSectionSlots?: { slug: string; node: ReactNode }[];
  /** The operator's own departure-date lines and BOTH WhatsApp surfaces, from
   *  Tours → Configuration → Tour Page. Icons arrive as resolved SVG paths
   *  because the departures list is a client component and the icon
   *  registry is a ~44KB server-only map.
   *
   *  This carries the WhatsApp links now. Two props used to: a resolved
   *  `whatsappChatUrl` for the `sites` route and a streamed
   *  `whatsappChatUrlPromise` for `(public)`, both reading the WhatsApp Widget
   *  app's agency-wide link. The tour page owns its own number as of
   *  2026-09-17, and `tourPage` is already awaited on both routes, so the
   *  promise plumbing and its `use()` inside the availability boundary are
   *  gone — one less thing to resolve before a departure row can render. */
  departureExtras?: DepartureExtras | null;
  /** The Ask a question dialog's title, from Tours → Configuration → Tour Page. */
  askQuestionHeading?: string;
  heroSlot?: ReactNode;
  /** The tour-facts band no longer has a slot of its own here. It is composed
   *  INTO the overview section by the page (`<DetailedDescription factsSlot>`)
   *  so it renders under that section's heading, where the guide puts it,
   *  rather than as a separate panel between the hero and the tab bar. */
  descriptionSlot?: ReactNode;
  itinerarySlot?: ReactNode;
  costSlot?: ReactNode;
  faqSlot?: ReactNode;
  reviewsSlot?: ReactNode;
  mapSlot?: ReactNode;
  relatedSlot?: ReactNode;
}

/** Fills the rail's grid track and matches the real card's height, so
 *  streaming the sidebar in second doesn't cause CLS. It no longer carries its
 *  own width: the track owns that now, and a skeleton that disagreed with the
 *  wrapper was how the swap shifted the page by 48px. */
function BookingSidebarSkeleton() {
  return <Skeleton variant="block" className={styles.sidebarSkeleton} />;
}

interface ResolvedSidebarBaseProps {
  data: ExperienceBookingData | null;
  travelers: TravelersState;
  setTravelers: Dispatch<SetStateAction<TravelersState>>;
  selectedDate: Date | undefined;
  setSelectedDate: (d: Date | undefined) => void;
  onCheckAvailability: () => void;
  showError: boolean;
  selectedPackageId: string | undefined;
  onPackageChange: (id: string) => void;
  /** Lifts the sidebar's live (paged) departures to the parent. */
  onDeparturesChange: (departures: DepartureDto[] | null) => void;
  /** Lifts the sidebar's live `hasMoreAfter` to the parent. */
  onHasMoreAfterChange: (hasMoreAfter: boolean) => void;
  askQuestionHeading?: string;
  /** Controls the quote-form dialog from outside the sidebar, so a click on
   *  a departure row or package card's "Request a Quote" opens the SAME
   *  dialog instance the sidebar's own button opens. */
  quoteFormDialogOpen: boolean;
  setQuoteFormDialogOpen: (open: boolean) => void;
}

interface PackagesSectionWithAvailabilityProps {
  availabilityPromise: Promise<ApiFetchResult<AvailabilityPayload>>;
  travelers: TravelersState;
  date: Date;
  onDateChange: (date: Date) => void;
  apiPackages: ExperienceDto["packages"];
  seasons: NonNullable<ExperienceDto["seasons"]>;
  tourSlug: string;
  tourTitle: string;
  heroImageUrl: string;
  experienceId: string;
  /** Trip length, so the add-to-cart confirmation states the end day too. */
  durationDays: number | null;
  /** The sidebar's live (paged) departures, lifted up. Falls back to the
   *  initial seed until the sidebar reports up — so the package cards and the
   *  calendar always agree on which dates have departures. */
  sharedDepartures: DepartureDto[] | null;
  /** Whether this page is rendering a "Dates & prices" section. The cards hang
   *  off the booking rail, not off that tab, so they can outlive it — and one
   *  of their dead-end fallbacks links to `#dates`. */
  hasDatesSection: boolean;
  /** Quote-on-request (AC-10): hides each card's price panel and swaps
   *  Book Now / Add to Cart for a single quote CTA. */
  isOnRequest: boolean;
  /** Opens the configured Button link action (doc §6.2) — the inquiry form
   *  popup or the custom link — same as the sidebar's own quote button. */
  onRequestQuote: () => void;
}

/** Real, or absent — matching the hero exactly. Both used to fall back to one
 *  shared mock, not for either surface's own sake but so the page could not
 *  contradict itself: a rail that went blank beside a rated hero read as
 *  "rated up there, unrated down here" on one screen. The hero's fallback is
 *  gone now, so this one goes with it for the same reason it existed. */
function resolveSidebarRating(
  data: ResolvedSidebarBaseProps["data"],
): { average: number; count: number } | undefined {
  return (
    (data?.rating && data.rating.count > 0 ? data.rating : null) ?? undefined
  );
}

/** Renders `<BookingSidebar>` from an ALREADY-resolved availability slice. The
 *  large prop fan-out lives here once and is shared by both the promise path
 *  (`SidebarWithAvailability`) and the live hook path. */
function ResolvedBookingSidebar({
  data,
  travelers,
  setTravelers,
  selectedDate,
  setSelectedDate,
  onCheckAvailability,
  showError,
  selectedPackageId,
  onPackageChange,
  onDeparturesChange,
  onHasMoreAfterChange,
  departures,
  windowEnd,
  hasMoreAfter,
  askQuestionHeading,
  quoteFormDialogOpen,
  setQuoteFormDialogOpen,
}: ResolvedSidebarBaseProps & {
  departures: DepartureDto[] | null;
  windowEnd: string | null;
  hasMoreAfter: boolean;
}) {
  return (
    <BookingSidebar
      travelers={travelers}
      setTravelers={setTravelers}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      onCheckAvailability={onCheckAvailability}
      showError={showError}
      experienceSlug={data?.slug}
      experienceId={data?.id}
      experienceName={data?.name}
      durationDays={data?.durationDays}
      askQuestionHeading={askQuestionHeading}
      quoteFormDialogOpen={quoteFormDialogOpen}
      setQuoteFormDialogOpen={setQuoteFormDialogOpen}
      departures={departures}
      initialWindowEnd={windowEnd}
      initialHasMoreAfter={hasMoreAfter}
      priceFrom={data?.priceFrom ?? undefined}
      packages={data?.packages}
      seasons={data?.seasons}
      pricingMode={data?.pricingMode}
      onRequest={data?.onRequest}
      hasGroupPricing={data?.hasGroupPricing}
      payment={data?.payment}
      badge={data?.badge}
      soldAs={data?.soldAs}
      pricingHighlights={data?.pricingHighlights}
      rating={resolveSidebarRating(data)}
      selectedPackageId={selectedPackageId}
      onPackageChange={onPackageChange}
      onDeparturesChange={onDeparturesChange}
      onHasMoreAfterChange={onHasMoreAfterChange}
    />
  );
}

/** Promise path (`(public)` + preview): unwraps the SSR availability promise
 *  via React 19 `use()`. Rendered DEEP inside the view behind its own
 *  `<Suspense>` so ONLY this subtree suspends while the promise pends — the
 *  hero + every server slot stream in the initial document. An availability
 *  error resolves to `departures: null` (unchanged) — the sidebar treats null
 *  as "no live slice", never a false "sold out" (which needs a non-null empty
 *  array). Render crashes bubble to the surrounding
 *  `<BookingSidebarErrorBoundary>`. */
function SidebarWithAvailability({
  availabilityPromise,
  ...rest
}: ResolvedSidebarBaseProps & {
  availabilityPromise: Promise<ApiFetchResult<AvailabilityPayload>>;
}) {
  const result = use(availabilityPromise);
  return (
    <ResolvedBookingSidebar
      {...rest}
      departures={result.ok ? result.data.departures : null}
      windowEnd={result.ok ? result.data.window.windowEnd : null}
      hasMoreAfter={result.ok ? result.data.window.hasMoreAfter : false}
    />
  );
}

/** Re-uses the same `availabilityPromise` already in-flight for the
 *  sidebar — React's `use()` deduplicates by promise reference, so this
 *  doesn't double-fetch. The packages section needs per-(date, time,
 *  package) `departures[]` to drive real `spotsLeft` and disable
 *  sold-out time chips per package. */
function PackagesSectionWithAvailability({
  availabilityPromise,
  sharedDepartures,
  ...props
}: PackagesSectionWithAvailabilityProps) {
  const result = use(availabilityPromise);
  const seed = result.ok ? result.data.departures : null;
  // Prefer the sidebar's live (paged-forward) departures so a date the
  // calendar enabled but the SSR window didn't include is never mislabelled
  // "not available". Fall back to the initial seed before the sidebar reports.
  const departures = sharedDepartures ?? seed;
  return <PackagesSection {...props} departures={departures} />;
}

/** Live path (`sites`): the initial slice is already resolved by the
 *  `useAvailability` hook and threaded down as `seedDepartures`. Precedence
 *  matches the promise path — the sidebar's paged-forward array wins. */
function LivePackagesSection({
  seedDepartures,
  sharedDepartures,
  ...props
}: Omit<PackagesSectionWithAvailabilityProps, "availabilityPromise"> & {
  seedDepartures: DepartureDto[] | null;
}) {
  const departures = sharedDepartures ?? seedDepartures;
  return <PackagesSection {...props} departures={departures} />;
}

function ExperienceDetailView({
  data,
  media,
  availability,
  tabs,
  visibleTabs,
  customSectionSlots,
  departureExtras,
  askQuestionHeading,
  heroSlot,
  descriptionSlot,
  itinerarySlot,
  costSlot,
  faqSlot,
  reviewsSlot,
  mapSlot,
  relatedSlot,
}: ExperienceDetailViewProps) {
  const t = useTranslator();
  const externalBooking = data?.externalBooking ?? null;
  const [travelers, setTravelers] = useState<TravelersState>(() =>
    seedTravelersFromPackage(data?.packages),
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  // Lifted out of the sidebar so every "Request a Quote" touchpoint on the
  // page (departure rows, package cards, the mobile sticky bar) can open the
  // SAME dialog instance the sidebar renders — see `handleRequestQuoteClick`.
  const [isQuoteFormDialogOpen, setIsQuoteFormDialogOpen] = useState(false);

  // Shared availability window: the sidebar (which owns the calendar and
  // pages departures forward) reports its live array up here, and the package
  // cards read the SAME array — so a date the calendar enabled is never
  // mislabelled "not available" in the cards. Null until the sidebar reports.
  const [sharedDepartures, setSharedDepartures] = useState<
    DepartureDto[] | null
  >(null);
  // Mirrors the sidebar's `hasMoreAfter` so the mobile sticky bar's
  // not-configured check (below) doesn't flag a tour "not scheduled" when
  // its only departures sit past the initial window but within the API's
  // availability horizon (e.g. a tour scheduled a year out).
  //
  // `null` (not `false`) until the sidebar first reports, so the "Dates &
  // prices" tab can tell "the sidebar hasn't weighed in yet — trust the SSR
  // seed" apart from "the sidebar weighed in and says there's nothing more".
  // A plain boolean defaulting to `false` can't make that distinction, and
  // OR-ing it with the seed (the bug this replaced) meant a seed of `true`
  // could never be overridden once the sidebar paged in the rest of the
  // schedule and correctly flipped to `false` — "More dates open further
  // ahead" stayed on screen forever, even once there genuinely were none.
  const [sharedHasMoreAfter, setSharedHasMoreAfter] = useState<boolean | null>(
    null,
  );
  const [isAvailabilityChecked, setIsAvailabilityChecked] = useState(false);
  const [showIncompleteError, setShowIncompleteError] = useState(false);
  const packagesRef = useRef<HTMLDivElement>(null);
  // Booking sidebar wrapper — sticky-bar CTA scrolls here on tap.
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Mobile hydration deferral for the resolved sidebar (live path only). Below
  // the split the sidebar sits under the whole article, so rendering it during
  // initial hydration burns main-thread time inside the LCP window for UI
  // nobody can see yet; the skeleton (already the pre-fetch state) holds the
  // space until the wrapper scrolls near. At or above the split it mounts
  // immediately — there the sidebar IS above the fold. Deliberately NOT a
  // next/dynamic chunk split — see the BookingSidebar import note above
  // (chunk-load failure must not leave dead UI); this defers only the render.
  //
  // MUST equal the `lg:` breakpoint the two-column grid turns on at (1024px).
  // While this said 1280 and the grid said 1024, every viewport in between saw
  // the rail in its own column — above the fold — showing a skeleton until
  // they scrolled, which on a tall enough window they never needed to do.
  // A departure reads as WORDS — "Sun, Oct 11, 2026" — and deliberately does
  // not follow the agency's numeric `dateFormat`. `11/10/2026` is a different
  // day to a reader in London than to one in New York, and this is the one
  // date on the page a traveller books flights around. `monthStyle` is the
  // formatter's existing opt-out from the numeric token.
  const formatAgencyDate = useFormatDate();
  const formatDepartureDate = useCallback(
    (iso: string) =>
      formatAgencyDate(iso, { weekday: "short", monthStyle: "short" }),
    [formatAgencyDate],
  );

  const [sidebarNear, setSidebarNear] = useState(false);
  useEffect(() => {
    if (sidebarNear) return;
    if (window.matchMedia("(min-width: 1024px)").matches) {
      setSidebarNear(true);
      return;
    }
    const el = sidebarRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setSidebarNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSidebarNear(true);
          io.disconnect();
        }
      },
      { rootMargin: "800px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [sidebarNear]);

  const defaultPkgId =
    data?.packages?.find((p) => p.isDefault)?.id ?? data?.packages?.[0]?.id;
  const [selectedPackageId, setSelectedPackageId] = useState<
    string | undefined
  >(defaultPkgId);

  // Stash the current tour's slug so the empty-cart CTA on /cart can send
  // the user back to the tour they were just viewing (rather than to the
  // generic listing page). Deferred to idle so the synchronous-DOM
  // localStorage call doesn't burn 1-3ms of post-paint INP budget.
  // Try/catch wraps the call so private mode / disabled storage doesn't
  // break the page render.
  useEffect(() => {
    if (!data?.slug) return;
    const ric =
      typeof window !== "undefined" && "requestIdleCallback" in window
        ? window.requestIdleCallback.bind(window)
        : (cb: () => void): number =>
            window.setTimeout(cb, 200) as unknown as number;
    const cic =
      typeof window !== "undefined" && "cancelIdleCallback" in window
        ? window.cancelIdleCallback.bind(window)
        : (id: number): void => window.clearTimeout(id);
    const handle = ric(() => {
      try {
        window.localStorage.setItem("tc_last_tour", data.slug);
      } catch {
        // ignore — non-critical UX hint
      }
    });
    return () => cic(handle as number);
  }, [data?.slug]);

  useEffect(() => {
    if (data?.id) trackAdsEvent({ name: "tour_viewed", contentId: data.id });
  }, [data?.id]);

  useEffect(() => {
    setTravelers(seedTravelersFromPackage(data?.packages));
    setSelectedPackageId(defaultPkgId);
  }, [defaultPkgId]);

  const handlePackageChange = (pkgId: string) => {
    setSelectedPackageId(pkgId);
    const pkg = data?.packages?.find((p) => p.id === pkgId);
    if (pkg) setTravelers(seedTravelersFromPackage([pkg]));
  };

  const handleCheckAvailability = () => {
    if (!selectedDate) {
      setShowIncompleteError(true);
      return;
    }
    setShowIncompleteError(false);
    setIsAvailabilityChecked(true);
    // rAF fires after React has committed the new <PackagesSection> to
    // the DOM, so the ref is attached. setTimeout(50) was a magic-number
    // workaround with the same intent but unreliable on slow CPUs.
    requestAnimationFrame(() => {
      packagesRef.current?.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "start",
      });
    });
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      setShowIncompleteError(false);
      setIsAvailabilityChecked(true);
      requestAnimationFrame(() => {
        packagesRef.current?.scrollIntoView({
          behavior: prefersReducedMotion() ? "auto" : "smooth",
          block: "start",
        });
      });
    } else {
      setIsAvailabilityChecked(false);
    }
  };

  const chooseAddressDate = useCallback((date: Date) => {
    setSelectedDate(date);
    setIsAvailabilityChecked(true);
  }, []);
  useDateFromAddress(
    availability.mode === "promised"
      ? availability.promise
      : availability.state.status === "pending"
        ? null
        : availability.state.departures,
    chooseAddressDate,
  );

  const handleStickyBarBook = () => {
    // Scroll the mobile booking widget (`#booking-widget`, the last block in the
    // article) to just under the sticky header. It sits below lazy images and
    // `content-visibility:auto` sections that keep expanding for a second or two
    // after the tap, so the target drifts down by thousands of px — a one-shot
    // scroll (especially `smooth`, whose destination is fixed at kickoff) lands
    // far short. Re-align on every page-height change until the content settles;
    // `scroll-mt-32` on the wrapper parks it under the header.
    const el = sidebarRef.current;
    if (!el) return;
    const align = () => el.scrollIntoView({ block: "start" });
    align();
    const observer = new ResizeObserver(align);
    observer.observe(document.body);
    let timer = 0;
    const release = () => {
      observer.disconnect();
      window.removeEventListener("touchmove", release);
      window.removeEventListener("wheel", release);
      window.clearTimeout(timer);
    };
    // Release the moment the user scrolls themselves — never fight their input.
    window.addEventListener("touchmove", release, { passive: true });
    window.addEventListener("wheel", release, { passive: true });
    // Backstop so the observer can't outlive the settle (normally released by
    // the first user scroll well before this fires).
    timer = window.setTimeout(release, 4000);
  };

  // AC-09: the quote button on departure rows and package cards follows the
  // SAME Button link setting as the sidebar's own quote box. Mirrors the
  // sidebar's own resolution order exactly (`_booking-sidebar.tsx`, the quote
  // CTA block): a custom link wins, then the legacy email/phone/whatsapp
  // actions, then the chosen inquiry form. Only a genuinely unconfigured
  // quote (no form, no legacy action) falls back to the scroll — same as
  // what the sidebar itself shows there: a disabled "Not available right
  // now" button, not a dead click.
  //
  // NOT used by the mobile sticky bar — that one always scrolls to the
  // sidebar instead (see its own `onBook` below), so the traveller sees the
  // quote box's pitch before the form opens.
  const handleRequestQuoteClick = () => {
    const action = resolveQuoteAction(data?.onRequest);
    switch (action.kind) {
      case "navigate":
        if (action.newTab) {
          window.open(action.url, "_blank", "noopener,noreferrer");
        } else {
          window.location.href = action.url;
        }
        return;
      case "email":
        window.location.href = `mailto:${action.address}`;
        return;
      case "tel":
        window.location.href = `tel:${action.number}`;
        return;
      case "whatsapp":
        window.open(
          `https://wa.me/${action.number.replace(/\D/g, "")}`,
          "_blank",
          "noopener,noreferrer",
        );
        return;
      case "form":
        setIsQuoteFormDialogOpen(true);
        return;
      case "unavailable":
        handleStickyBarBook();
        return;
    }
  };

  const showTabBar = (tabs?.length ?? 0) > 0;

  // Dates & prices. Two paths, for the same reason the sidebar has two: the
  // `sites` route resolves availability through a hook, `(public)` through a
  // promise that must be unwrapped with `use()` behind its own Suspense so the
  // rest of the page still streams.
  //
  // Both prefer `sharedDepartures` — the paged-forward array the sidebar
  // reports up — over their own initial seed, so a date the calendar has
  // scrolled to is never missing from the list below it.

  // The SAME chain the rail runs, run here too — the rail is not mounted yet on
  // a phone at the top of the page (`sidebarNear`), and the pinned bar has to
  // show a real figure from the first paint. See `_active-pricing.ts` for why
  // it is shared rather than copied.
  //
  // MEMOISED, because this component re-renders on every `+`/`−` tap, every
  // date pick and every month change, and the chain walks the package's
  // categories four times over (season → package → totals → head count) and
  // allocates a fresh `categories` array each pass. It is also what gives
  // `totals` a stable identity, so the pinned bar below does not re-render for
  // a value that did not change.
  const { rawPkg: activePackage, totals: partyTotals } = useMemo(
    () =>
      resolveBookingPricing({
        packages: data?.packages,
        seasons: data?.seasons,
        selectedDate,
        selectedPackageId,
        travelers,
      }),
    [data?.packages, data?.seasons, selectedDate, selectedPackageId, travelers],
  );

  // AC-10: on an on-request tour, "Deposit today" and "Pay {amount} per
  // traveler today" are both price-derived figures (a percentage of
  // `priceFrom`) — nulling the one value here hides both the departure row's
  // deposit line and the "See more details" panel's deposit fact, rather than
  // repeating the on-request check at each of those two call sites.
  const depositPercentForDates =
    data?.pricingMode === "onrequest"
      ? null
      : (data?.payment?.depositPercent ?? null);
  const datesShared = {
    durationDays: data?.durationDays ?? null,
    depositPercent: depositPercentForDates,
    facts: {
      depositPercent: depositPercentForDates,
      balanceDueDaysBefore: data?.payment?.balanceDueDaysBefore ?? null,
    },
    extras: departureExtras ?? undefined,
    // The smallest party the operator will run. Feeds two things: the
    // "Guaranteed to run" badge, and the floor of each row's own group-size
    // line (`departure-row.tsx` pairs it with that row's `capacity`).
    //
    // THE PACKAGE'S OWN `minParticipants`, never the tour-wide `groupSize.min`.
    // The API built `groupSize` as the literal `{ min: 1, max: 10 }` for every
    // tour until 2026-09-20, so reading the minimum from there made the badge
    // mean "one seat sold" on every tour. `resolveMinParticipants` takes the
    // larger of the operator's `minPaxPerBooking` and the sum of their category
    // floors, and is null when they set neither, which correctly yields no
    // badge.
    minParticipants: activePackage?.minParticipants ?? null,
    // An ON-REQUEST tour does not confirm instantly — the operator answers
    // first — so the promise ribbon is suppressed rather than asserting the
    // opposite of what the tour is. See `PromiseBadges`.
    instantConfirmation: data?.pricingMode !== "onrequest",
    // AC-10: hides the price figure and swaps "Book now" for the Button link
    // action (doc §6.2) — see `departure-row.tsx`. `onRequestQuote` is
    // `handleRequestQuoteClick`, the same open-form/open-link resolution the
    // sidebar's own quote button uses, not a scroll to it.
    //
    // `pricingMode`, not `!!data?.onRequest` — `onRequestConfig` is schema-
    // optional (packages/shared/.../pricing.schema.ts), so a legacy or
    // not-yet-configured on-request tour can have `pricingMode: "onrequest"`
    // with no config object. `PackagesSection` and `StickyBookBar` already
    // key off `pricingMode`; checking the config's truthiness here let this
    // section disagree with them on the same page.
    isOnRequest: data?.pricingMode === "onrequest",
    onRequestQuote: handleRequestQuoteClick,
    onSelectDate: handleDateSelect,
    // The agency's own date format, timezone and locale — not
    // `toLocaleDateString`, which would ignore every one of those and print a
    // different date to the operator than the one they scheduled.
    formatDate: formatDepartureDate,
  };
  const datesSection =
    availability.mode === "promised" ? (
      <Suspense fallback={<DeparturesSkeleton />}>
        <DeparturesWithAvailability
          availabilityPromise={availability.promise}
          sharedDepartures={sharedDepartures}
          sharedHasMoreAfter={sharedHasMoreAfter}
          {...datesShared}
        />
      </Suspense>
    ) : (
      <DeparturesSection
        departures={
          sharedDepartures ??
          (availability.state.status === "pending"
            ? null
            : availability.state.departures)
        }
        hasMoreAfter={sharedHasMoreAfter ?? availability.state.hasMoreAfter}
        {...datesShared}
      />
    );

  // Where the booking rail cuts the body in two.
  //
  // On DESKTOP the rail is a column, so this split is invisible: the three
  // wrappers below are placed explicitly into column one, rows one to three,
  // and read as one continuous column exactly as the single `<div>` they
  // replaced did. On a PHONE there is no second column, so the split is the
  // whole point — the wrappers stack in DOM order and the rail lands directly
  // above the departures, which is where the mobile guide puts it.  It was at
  // the very bottom of the page, after every section, which on a 12,000px
  // document is somewhere nobody scrolls to on purpose.
  //
  // Data-driven section registry (AC-16/18): each tab slug resolves to its
  // server-rendered section, and they render in the operator's configured
  // order. EVERY body section is in here — including `dates` and `included`,
  // which used to be spliced in afterwards relative to a neighbouring tab
  // because they had no tab of their own. They are ordinary built-in tabs as
  // of 2026-09-22, so the operator's order is the page's order, full stop, and
  // the tab strip and the body cannot describe different pages.
  //
  // This sits HERE, below the slots, rather than higher up: `datesSection` is
  // built from the availability promise a few lines above and cannot be
  // referenced before it exists.
  const orderedSections = orderSections(
    tabs,
    {
      overview: descriptionSlot,
      dates: datesSection,
      itinerary: itinerarySlot,
      included: costSlot,
      maps: mapSlot,
      reviews: reviewsSlot,
      faqs: faqSlot,
    },
    new Map((customSectionSlots ?? []).map((s) => [s.slug, s.node])),
    visibleTabs,
  );

  // FAQs leave the two-column grid and render as a full-width band (guide
  // `.faqband`), so the page SPLITS AROUND them: the sections the operator
  // ordered above the band share the grid with the booking rail, the band spans
  // the page, and the rest render below it.
  //
  // It used to be filtered out of the body and pinned after everything, which
  // is why dragging it in Tour Tabs did nothing and why every custom tab
  // rendered above it despite sorting below it. `layoutAroundFaq` carries the
  // full reasoning and the tests.
  //
  // When FAQs is last — the seeded arrangement — `sectionsAfterFaq` is empty
  // and this lays out exactly as it did before.
  //
  // THE RAIL FOLLOWS THE MAIN BODY, so an operator cannot strand it by
  // reordering. Dragging FAQs to position 1 leaves nothing above the band, and
  // rendering an empty first block still rendered the grid — the 372px rail
  // alone in column two with ~600px of blank column one beside it. With
  // nothing above the band there is nothing to split: `bandLeads` puts the
  // band first and everything else in one ordinary grid below it.
  const {
    bandLeads,
    main: body,
    trailing: sectionsAfterFaq,
    faq: faqSection,
  } = layoutAroundFaq(orderedSections);
  // Where the booking rail sits in the MOBILE stack. On desktop the rail is its
  // own grid column and this split is invisible; on a phone the wrappers stack
  // in DOM order, so whatever lands in the first one is what a traveller
  // scrolls past before reaching the widget.
  //
  // The departures section is the natural anchor — the rail belongs beside it.
  // A tour can now legitimately have no departures section at all (never
  // priced, so the content gate drops the tab; or the operator switched it
  // off), and the fallback is "just after the first body section", which keeps
  // the widget near the top where it was.
  //
  // The fallback used to be `body` wholesale, which put the ENTIRE page above
  // the rail — every section, then the booking widget. That is the long-scroll
  // problem this three-wrapper split exists to prevent, reintroduced through
  // the one branch nothing could reach until `dates` became droppable.
  // `splitBodyForRail` is a pure function so that case has a test.
  const {
    before: sectionsBeforeRail,
    dates: datesEntry,
    after: sectionsAfterRail,
  } = splitBodyForRail(body);
  // An external tour never reaches the cart, so the package cards (Book Now /
  // Add to Cart) stay closed.
  const showPackages =
    isAvailabilityChecked && !!selectedDate && !!data && !externalBooking;
  const hasBodyBeforeRail = showPackages || sectionsBeforeRail.length > 0;
  const hasBodyAfterRail = sectionsAfterRail.length > 0;

  // Shared sidebar props — identical for the promise path and the live path;
  // the two differ only in how the resolved departures/window are supplied.
  const sidebarShared: ResolvedSidebarBaseProps = {
    data,
    travelers,
    setTravelers,
    selectedDate,
    setSelectedDate: handleDateSelect,
    onCheckAvailability: handleCheckAvailability,
    showError: showIncompleteError,
    selectedPackageId,
    onPackageChange: handlePackageChange,
    onDeparturesChange: setSharedDepartures,
    onHasMoreAfterChange: setSharedHasMoreAfter,
    askQuestionHeading,
    quoteFormDialogOpen: isQuoteFormDialogOpen,
    setQuoteFormDialogOpen: setIsQuoteFormDialogOpen,
  };

  // Mirror the sidebar's "not configured yet" state so the mobile sticky bar
  // never invites "Choose date" while the sidebar shows "Booking opens soon".
  // Uses `sharedDepartures` (the array the sidebar reports up) so both agree.
  const { isNotConfigured: bookingNotConfigured } = resolveBookingConfigState({
    packages: data?.packages,
    selectedPackageId,
    pricingMode: data?.pricingMode,
    departures: sharedDepartures,
    hasMoreAfter: sharedHasMoreAfter ?? false,
  });

  return (
    <ExperienceMediaGalleryProvider
      media={media}
      title={data?.name ?? t("Photo Gallery")}
    >
      {/* WHITE, not `--surface-page`. The guide's shared component set paints
          `body` with `--bg` (#f4f4f5), and this page overrides it to
          `--surface` (#ffffff) — a deliberate, page-scoped override, under its
          own "Single tour page" banner in the stylesheet.
          The override belongs HERE, on the page, and not on the token: moving
          `--surface-page` would repaint every other storefront route and the
          whole operator dashboard along with it.
          What makes white work is that every card already carries a border
          rather than relying on the page tint for its edge, and the page keeps
          exactly ONE tinted full-width band — the FAQ band (`--sunk` #fafafa,
          hairline top and bottom). Similar tours takes a top border and no
          fill. Add a second tinted band and the FAQ band stops reading as
          deliberate. */}
      <div className={styles.page}>
        {heroSlot}

        {/* The tab bar sits DIRECTLY under the hero's assurance strip, with
            nothing between them. It used to have the "At a Glance" facts panel
            above it, which read as a section the tabs did not index — and made
            the bar look like it belonged to the middle of the page rather than
            to the whole of it. Those facts now open the Overview section, where
            the guide puts them, and reach it via `infoSlot` threaded into
            `descriptionSlot` by the page. */}
        {showTabBar ? (
          <TourTabsBar tabs={tabs!} shareTitle={data?.name} />
        ) : null}

        {/* Guide `.pg-grid`: one fluid column beside a fixed 372px rail, 60px
            apart, collapsing to a single column on a tablet. A grid rather
            than a flex row because the rail is a FIXED width — expressing that
            as a flex row with a fixed width needs `flex-shrink: 0` on one side
            and `min-width: 0` on the other to stop the prose from pushing the rail
            narrow, and forgetting either is invisible until a long word
            appears. `minmax(0,1fr)` says it once.

            FOUR grid items, not two. On desktop the extra seams are invisible:
            the three body wrappers are placed explicitly into column one, rows
            one to three, and read as the single continuous column they
            replaced. On a phone there is no second column and the seams are
            the point — the wrappers stack in DOM order, so the rail lands
            between Overview and the departures, which is where the mobile
            guide puts it. It used to sit after every section on the page, at
            the foot of a 12,000px document.

            Three explicit rows at `lg` are what make the rail's three-row span
            mean anything: a span counts EXPLICIT rows, and a grid whose rows
            are all implicit has none to count. */}
        {/* The band OPENS the page when the operator ordered FAQs first —
            before the grid, not after it. See `layoutAroundFaq`: with nothing
            above the band there is nothing to split, so the grid below holds
            the whole body and keeps its rail. */}
        {bandLeads ? faqSection?.node : null}

        <div className={cx(SITE_CONTAINER, styles.body)}>
          <div className={styles.grid}>
            {/* Column one, part one: the packages (once a date is picked) plus
                every section up to and including Overview.

                `min-width: 0` is the load-bearing part: a grid item's default
                `min-width: auto` lets its content set a floor, so one long
                word or a wide table in the prose would push this column past
                its track and overflow the page on a phone. */}
            {hasBodyBeforeRail ? (
              <div className={cx(styles.column, styles.beforeRail)}>
                {showPackages ? (
                  <div ref={packagesRef} className={styles.packages}>
                    <Suspense fallback={<PackagesSkeleton />}>
                      {availability.mode === "promised" ? (
                        <PackagesSectionWithAvailability
                          availabilityPromise={availability.promise}
                          travelers={travelers}
                          date={selectedDate!}
                          onDateChange={handleDateSelect}
                          apiPackages={data!.packages ?? []}
                          seasons={data!.seasons ?? []}
                          tourSlug={data!.slug}
                          tourTitle={data!.name}
                          heroImageUrl={data!.heroImage.url}
                          experienceId={data!.id}
                          durationDays={datesShared.durationDays}
                          sharedDepartures={sharedDepartures}
                          hasDatesSection={!!datesEntry}
                          isOnRequest={data?.pricingMode === "onrequest"}
                          onRequestQuote={handleRequestQuoteClick}
                        />
                      ) : (
                        <LivePackagesSection
                          seedDepartures={availability.state.departures}
                          travelers={travelers}
                          date={selectedDate!}
                          onDateChange={handleDateSelect}
                          apiPackages={data!.packages ?? []}
                          seasons={data!.seasons ?? []}
                          tourSlug={data!.slug}
                          tourTitle={data!.name}
                          heroImageUrl={data!.heroImage.url}
                          experienceId={data!.id}
                          durationDays={datesShared.durationDays}
                          sharedDepartures={sharedDepartures}
                          hasDatesSection={!!datesEntry}
                          isOnRequest={data?.pricingMode === "onrequest"}
                          onRequestQuote={handleRequestQuoteClick}
                        />
                      )}
                    </Suspense>
                  </div>
                ) : null}
                {sectionsBeforeRail.map(({ slug, node }) => (
                  <Fragment key={slug}>{node}</Fragment>
                ))}
              </div>
            ) : null}

            {/* The booking rail. Width comes from the grid track (372px), so
                this carries no width of its own. The 124px top is the guide's own
                `.rail { top: 124px }` — the site header (64px) plus the sticky
                tab bar (~46px) plus a gap, so the card's top border stays
                visible instead of tucking under the bar.

                Everything about the sticky column is `lg`-scoped. On a phone
                the rail is an ordinary block in the stack; pinning a 480px card
                to a 844px viewport would leave the page four fifths covered. */}
            <div ref={sidebarRef} className={styles.rail}>
              {externalBooking && data ? (
                <ExternalBookingRail
                  booking={externalBooking}
                  priceFrom={data.priceFrom}
                  priceSuffix={data.headlinePriceLabel}
                />
              ) : (
                <BookingSidebarErrorBoundary>
                  {availability.mode === "promised" ? (
                    // Mobile: hold the skeleton (same dims → no CLS) until the
                    // wrapper scrolls near; on desktop `sidebarNear` is set on
                    // mount so this renders immediately. Defers the sidebar's
                    // hydration + `use(promise)` unwrap out of the LCP window for
                    // a widget that sits below the fold on mobile. Mirrors the
                    // live path's `sidebarNear` gate below.
                    !sidebarNear ? (
                      <BookingSidebarSkeleton />
                    ) : (
                      <Suspense fallback={<BookingSidebarSkeleton />}>
                        <SidebarWithAvailability
                          availabilityPromise={availability.promise}
                          {...sidebarShared}
                        />
                      </Suspense>
                    )
                  ) : availability.state.status === "pending" ||
                    !sidebarNear ? (
                    // Live path: reserve the sidebar's space (no CLS) until the
                    // client fetch settles AND (on mobile) the wrapper scrolls
                    // near — see the sidebarNear note above. State-driven — no
                    // Suspense needed.
                    <BookingSidebarSkeleton />
                  ) : (
                    <ResolvedBookingSidebar
                      {...sidebarShared}
                      departures={availability.state.departures}
                      windowEnd={availability.state.windowEnd}
                      hasMoreAfter={availability.state.hasMoreAfter}
                    />
                  )}
                </BookingSidebarErrorBoundary>
              )}
            </div>

            {/* Dates & prices gets its own wrapper because the booking rail
                sits beside it, not because it is a special kind of section —
                it is an ordinary built-in tab (`builtinTourTabKeySchema`), in
                whatever position the operator put it, and absent entirely when
                the tour has no packages or the operator switched the tab off.
                `splitBodyForRail` decides where the rail lands in that case.

                The top border replaces the rule this section used to get from
                `section + section`, which cannot reach across the wrapper
                boundary the rail introduced.
                Skipped when nothing precedes it, so an operator who hid
                Overview does not get a rule hanging above the first thing on
                the page. */}
            {datesEntry ? (
              <div
                className={cx(
                  styles.column,
                  styles.dates,
                  hasBodyBeforeRail && styles.columnDivided,
                )}
              >
                {datesEntry.node}
              </div>
            ) : null}

            {/* Column one, part three: everything the operator's tab order puts
                after the departures — "What's included", Reviews and any
                custom tab, each wherever the operator ordered it. */}
            {hasBodyAfterRail ? (
              <div
                className={cx(
                  styles.column,
                  styles.columnDivided,
                  styles.afterRail,
                )}
              >
                {sectionsAfterRail.map(({ slug, node }) => (
                  <Fragment key={slug}>{node}</Fragment>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {/* Guide `.faqband` — full width, its own tint, outside the grid. It
            carries its own container, so no wrapper here.

            Skipped when `bandLeads`, where it has already rendered above the
            grid; rendering it in both places would draw it twice. */}
        {bandLeads ? null : faqSection?.node}

        {/* Whatever the operator ordered BELOW the FAQ band. Its own grid,
            with the SAME column template as the one above — the rail has
            already ended, so the second track stays empty, but keeping the
            template means the prose measure does not change across the band.
            A section that jumped from 784px to full width on the way past the
            FAQs would read as the page breaking. */}
        {sectionsAfterFaq.length > 0 ? (
          <div className={cx(SITE_CONTAINER, styles.body)}>
            <div className={styles.trailingGrid}>
              <div className={cx(styles.column, styles.trailing)}>
                {sectionsAfterFaq.map(({ slug, node }) => (
                  <Fragment key={slug}>{node}</Fragment>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {/* Guide `.simband` — a FULL-WIDTH band outside the two-column grid,
            with its own container. Inside the left column it was 784px wide
            and its three cards had to share that with a rail that had run out
            of content to sit beside, which is the one place on the page where
            the grid has nothing left to justify itself. */}
        <div className={SITE_CONTAINER}>{relatedSlot}</div>

        <StickyBookBar
          fromPrice={
            hasDisplayPrice(data?.priceFrom) ? data?.priceFrom : undefined
          }
          wasPrice={data?.priceFromWas}
          priceSuffix={data?.headlinePriceLabel}
          partyTotals={externalBooking ? null : partyTotals}
          notConfigured={!externalBooking && bookingNotConfigured}
          externalBooking={externalBooking}
          // Scrolls to the sidebar on EVERY pricing mode, on-request included.
          // This bar is a shortcut to the booking widget, not a second action
          // surface — the quote box living in that widget has its own
          // "Request a quote" button (`handleRequestQuoteClick`, AC-09/AC-15),
          // the same one departure rows and package cards already use. Used
          // to open the form/link directly from here on the reasoning that
          // the sidebar was "a phone screen below" and so invisible — but a
          // tap that pops a multi-field form before the traveller has seen
          // the quote box's own pitch (starting price, highlights, trust
          // copy) reads as a request they didn't ask for, and on a tour that
          // also has departure dates (AC-10) it skips past those entirely.
          // `scrollIntoView` goes to that content directly regardless of
          // distance; nothing here needed its own path to the destination.
          onBook={handleStickyBarBook}
          onRequest={
            data?.pricingMode === "onrequest" ? data.onRequest : undefined
          }
        />

        {/* The app-shell bottom bar. Built from `orderedSections` — the SAME
            list the page renders from, so the bar's order is the page's order
            is the operator's order, and the three cannot drift. It was
            `[...orderedSections, "dates"]`, which appended Dates after Reviews
            while the page draws it second: the highlight then ran backwards
            along the bar as the reader scrolled forwards.

            It then read `body`, which is only the sections ABOVE the FAQ band.
            Which slugs get a button is `BOTTOM_TAB_ICONS`'s decision, and that
            table does not list `faqs` — so this changes nothing on screen
            today. It passes the whole page order because that is what the
            comment above claims, and a curated bar should do its own curating
            rather than inherit someone else's truncation. */}
        <TourBottomTabs
          sectionSlugs={orderedSections.map((entry) => entry.slug)}
          tabs={tabs ?? []}
        />
      </div>
    </ExperienceMediaGalleryProvider>
  );
}

/** Holds the departure list's space while the availability promise settles, so
 *  the sections below it do not jump when the rows arrive. */
function DeparturesSkeleton() {
  return (
    <div className={styles.departuresSkeleton}>
      {[0, 1, 2].map((i) => (
        <Skeleton
          key={i}
          variant="block"
          className={styles.departureSkeleton}
        />
      ))}
    </div>
  );
}

/** Promise path (`(public)` + preview): unwraps the SSR availability promise
 *  via React 19 `use()`, behind the caller's own `<Suspense>`. Re-uses the same
 *  promise already in flight for the sidebar — `use()` deduplicates by promise
 *  reference, so this does not double-fetch.
 *
 *  An availability error resolves to `departures: null`, which the section
 *  renders as "checking", never as "no departures scheduled". The two mean
 *  opposite things and only one of them is a fact we have. */
function DeparturesWithAvailability({
  availabilityPromise,
  sharedDepartures,
  sharedHasMoreAfter,
  ...rest
}: Omit<
  ComponentProps<typeof DeparturesSection>,
  "departures" | "hasMoreAfter"
> & {
  availabilityPromise: Promise<ApiFetchResult<AvailabilityPayload>>;
  sharedDepartures: DepartureDto[] | null;
  sharedHasMoreAfter: boolean | null;
}) {
  const result = use(availabilityPromise);
  const seed = result.ok ? result.data.departures : null;
  return (
    <DeparturesSection
      departures={sharedDepartures ?? seed}
      hasMoreAfter={
        sharedHasMoreAfter ??
        (result.ok ? result.data.window.hasMoreAfter : false)
      }
      {...rest}
    />
  );
}

/** Live-availability wrapper — used by the params-driven `sites` route so its
 *  render path creates NO per-request server availability fetch (prerequisite
 *  for Task 5 ISR). Fetches the initial window client-side via
 *  `useAvailability`; the sidebar shows `BookingSidebarSkeleton` (same
 *  dimensions → no CLS) until it lands.
 *
 *  Separate from `ExperienceDetailWithPromisedAvailability` because Rules of
 *  Hooks forbid a single component conditionally choosing between the
 *  `useAvailability` hook and `use(promise)`. */
export function ExperienceDetailWithLiveAvailability({
  experienceSlug,
  ...viewProps
}: Omit<ExperienceDetailViewProps, "availability"> & {
  experienceSlug: string | undefined;
}) {
  const state = useAvailability(experienceSlug);
  return (
    <ExternalBookingContext.Provider
      value={viewProps.data?.externalBooking ?? null}
    >
      <ExperienceDetailView
        {...viewProps}
        availability={{ mode: "live", state }}
      />
    </ExternalBookingContext.Provider>
  );
}

/** Promised-availability wrapper — used by `(public)` + preview. Passes the
 *  server-created promise straight through; it is unwrapped via `use()` DEEP
 *  at the sidebar/package boundaries (inside the view), so the hero + every
 *  server slot stream in the initial document exactly as before this refactor.
 *  Preview keeps this path because its draft availability comes from a
 *  token-gated endpoint the public client route cannot serve. */
export function ExperienceDetailWithPromisedAvailability({
  availabilityPromise,
  ...viewProps
}: Omit<ExperienceDetailViewProps, "availability"> & {
  availabilityPromise: Promise<ApiFetchResult<AvailabilityPayload>>;
}) {
  return (
    <ExternalBookingContext.Provider
      value={viewProps.data?.externalBooking ?? null}
    >
      <ExperienceDetailView
        {...viewProps}
        availability={{ mode: "promised", promise: availabilityPromise }}
      />
    </ExternalBookingContext.Provider>
  );
}
